<template>
    <div
        class="file-uploader"
        data-test-id="drag-drop-field"
    >
        <FileUpload
            ref="fileUpload"
            name="image"
            :multiple="true"
            accept="image/*"
            :maxFileSize="31457280"
            :customUpload="true"
            mode="advanced"
            :pt="fileUploadPassthrough"
            :showUploadButton="false"
            :showCancelButton="false"
            @select="onFileSelect"
        >
            <template #header="{ chooseCallback }">
                <button
                    class="btn btn-primary choose-image-btn"
                    type="button"
                    data-test-id="cta-choose-file-button"
                    @click="chooseCallback()"
                >
                    <img
                        src="/images/add-black.svg"
                        alt="close"
                    />
                    Choose
                </button>
            </template>
            <template #content="{ removeFileCallback, messages }">
                <Message
                    v-for="msg of messages"
                    :key="msg"
                    severity="error"
                    >{{ msg }}</Message
                >
                <div
                    class="image-list"
                    data-test-id="image-list"
                >
                    <div
                        v-for="(image, index) in files"
                        :key="index"
                        class="image-item"
                        :data-test-id="`selected-image-row-${index}`"
                    >
                        <div class="image-data">
                            <div class="star">
                                <button
                                    v-if="!image.isNew || image.newFileData.state === fileStates.COMPLETE"
                                    class="cover-image-button"
                                    title="Toggle cover image"
                                    type="button"
                                    :data-test-id="`cta-cover-image-button-${index}`"
                                    @click="toggleCoverImage(image)"
                                >
                                    <img
                                        :src="coverImageIcon(image)"
                                        :data-test-id="`cta-cover-image-button-image-${index}`"
                                    />
                                </button>
                            </div>
                            <div
                                class="image"
                                :data-test-id="`selected-image-thumbnail-${index}`"
                                :style="{ backgroundImage: `url(${image.url})` }"
                            ></div>
                            <div
                                v-if="image.isNew"
                                class="info"
                            >
                                <span
                                    class="name"
                                    :data-test-id="`selected-image-name-${index}`"
                                    >{{ image.newFileData.file.name }}</span
                                >
                                <span
                                    class="size"
                                    :data-test-id="`selected-image-size-${index}`"
                                    >{{ formatSize(image.newFileData.file.size) }}</span
                                >
                            </div>
                            <div
                                v-if="image.isNew"
                                class="status"
                                :class="{
                                    uploading: image.newFileData.state === 'uploading',
                                    complete: image.newFileData.state === 'complete',
                                    error: image.newFileData.state === 'error',
                                }"
                                :data-test-id="`selected-image-status-${index}`"
                            >
                                {{ stateLabelForFile(image) }}
                            </div>
                            <button
                                class="delete-image-button"
                                :data-test-id="`cta-remove-selected-image-${index}`"
                                type="button"
                                @click="confirmRemoveImage(removeFileCallback, index)"
                            >
                                <div class="image-delete-container">
                                    <img
                                        src="/images/close-white.svg"
                                        alt="close"
                                    />
                                </div>
                            </button>
                        </div>
                        <div
                            v-if="image.isNew && image.newFileData.state === fileStates.UPLOADING"
                            class="progress-container"
                        >
                            <ProgressBar
                                :data-test-id="`selected-image-progress-bar-${index}`"
                                :value="image.newFileData.uploadProgress"
                                :showValue="false"
                            ></ProgressBar>
                        </div>
                    </div>
                </div>
            </template>
            <template #empty>
                <div
                    class="drop-zone"
                    data-test-id="drag-drop-target"
                    @dragover.prevent="onDragOver"
                >
                    Drag and drop files here to upload.
                </div>
            </template>
        </FileUpload>
    </div>
</template>
<script>
import FileUpload from 'primevue/fileupload'
import ProgressBar from 'primevue/progressbar'
import Message from 'primevue/message'
import canConfirm from '@/mixins/canConfirm'
import needsOrganizationSlug from '@/mixins/needsOrganizationSlug'

const FILE_STATES = {
    INITIAL: 'initial',
    UPLOADING: 'uploading',
    COMPLETE: 'complete',
    ERROR: 'error',
}

export default {
    components: {
        FileUpload,
        Message,
        ProgressBar,
    },

    mixins: [canConfirm, needsOrganizationSlug],

    props: {
        existingImages: {
            type: Array,
            default: () => [],
        },
    },
    emits: ['cover-image-set', 'file-removed', 'file-uploaded', 'uploading', 'uploading-complete'],

    data() {
        return {
            fileStates: FILE_STATES,
            files: this.existingImages.map((item) => ({
                isCoverImage: item.is_cover_image,
                id: item.id,
                url: item.url,
                isNew: false,
                newFileData: null,
            })),
            fileUploadPassthrough: {
                root: {
                    style: {
                        backgroundColor: 'transparent',
                        borderWidth: 0,
                    },
                },
                header: {
                    style: {
                        padding: 0,
                    },
                },
                content: {
                    style: {
                        borderWidth: 0,
                        padding: 0,
                        margin: 0,
                        paddingTop: '.5rem',
                    },
                },
                empty: {
                    style: {
                        backgroundColor: 'transparent',
                        color: 'white',
                        borderRadius: '8px',
                    },
                },
            },
        }
    },

    watch: {
        existingImages: {
            handler(newVal) {
                const existingFiles = newVal.map((item) => ({
                    isCoverImage: item.is_cover_image,
                    id: item.id,
                    url: item.url,
                    isNew: false,
                    newFileData: null,
                }))

                this.files = [...existingFiles, ...this.files.filter((item) => item.isNew)]
            },
            deep: true,
        },
        files(newVal, oldVal) {
            const wasUploading = this.areFilesUploading(oldVal)
            const isUploading = this.areFilesUploading(newVal)

            if (!wasUploading && isUploading) {
                this.emit('uploading')
            } else if (wasUploading && !isUploading) {
                this.emit('uploading-complete')
            }
        },
    },

    mounted() {
        // Add drag-and-drop event listeners to the document
        document.addEventListener('dragenter', this.onPageDragEnter, false)
        document.addEventListener('dragover', this.preventDefaults, false)
        document.addEventListener('dragleave', this.onPageDragLeave, false)
        document.addEventListener('drop', this.onPageDrop, false)
    },
    unmounted() {
        // Clean up event listeners
        document.removeEventListener('dragenter', this.onPageDragEnter, false)
        document.removeEventListener('dragover', this.preventDefaults, false)
        document.removeEventListener('dragleave', this.onPageDragLeave, false)
        document.removeEventListener('drop', this.onPageDrop, false)

        this.files.forEach((file) => {
            URL.revokeObjectURL(file.url)
        })
    },

    methods: {
        areFilesUploading(files) {
            return files
                .filter(({ isNew }) => isNew)
                .some(
                    (file) =>
                        file.newFileData.state === FILE_STATES.INITIAL &&
                        file.newFileData.state === FILE_STATES.UPLOADING
                )
        },

        selectDefaultCoverImage() {
            // if no image is selected as the cover image, it should default
            // to the first fully uploaded file
            const coverImageIndex = this.files.findIndex((file) => file.isCoverImage)
            if (coverImageIndex === -1) {
                // find first possible cover image
                const potentialCoverImage = this.files.find(
                    (file) =>
                        !file.isNew ||
                        file.newFileData.state === FILE_STATES.INITIAL ||
                        file.newFileData.state === FILE_STATES.UPLOADING ||
                        file.newFileData.state === FILE_STATES.COMPLETE
                )

                // set as cover image if fully uploaded
                if (!potentialCoverImage?.isNew || potentialCoverImage?.newFileData.state === FILE_STATES.COMPLETE) {
                    potentialCoverImage.isCoverImage = true
                    this.$emit('cover-image-set', potentialCoverImage.id)
                }
            }
        },
        // Prevent default browser behavior for drag-and-drop
        preventDefaults(event) {
            event.preventDefault()
            event.stopPropagation()
            event.dataTransfer.dropEffect = 'none' // Suppress "add to page" cursor
        },
        // Handle dragenter on the page
        onPageDragEnter(event) {
            event.preventDefault()
            event.stopPropagation()
        },
        // Handle dragleave on the page
        onPageDragLeave(event) {
            event.preventDefault()
            event.stopPropagation()
        },
        // Handle drop on the page (outside drop zone)
        onPageDrop(event) {
            event.preventDefault()
            event.stopPropagation()
        },
        // Handle dragover on the drop zone
        onDragOver(event) {
            event.preventDefault() // Necessary for drop to work
            event.dataTransfer.dropEffect = 'copy' // Show "copy" cursor over drop zone
        },
        stateLabelForFile(file) {
            switch (file.newFileData.state) {
                case FILE_STATES.INITIAL:
                case FILE_STATES.UPLOADING:
                    return 'Pending'

                case FILE_STATES.COMPLETE:
                    return 'Complete'

                case FILE_STATES.ERROR:
                default:
                    return 'Error'
            }
        },
        coverImageIcon(file) {
            return file.isCoverImage ? '/images/star-filled.svg' : '/images/star-unfilled.svg'
        },

        toggleCoverImage(file) {
            if (file.isCoverImage) {
                file.isCoverImage = false
                this.selectDefaultCoverImage()
            } else {
                this.files.forEach((tmpFile) => {
                    tmpFile.isCoverImage = false
                })

                file.isCoverImage = true
                this.$emit('cover-image-set', file.id)
            }
        },

        onFileSelect(event) {
            event.files
                .filter(({ objectURL }) => this.files.findIndex(({ url }) => url === objectURL) === -1)
                .forEach((file) => {
                    const fileItem = {
                        isCoverImage: false,
                        id: this.$uuid.v4(),
                        url: file.objectURL,
                        isNew: true,
                        newFileData: {
                            file,
                            state: FILE_STATES.INITIAL,
                            uploadProgress: null,
                            abortController: new AbortController(),
                        },
                    }

                    const last = this.files.push(fileItem)
                    this.upload(this.files[last - 1])
                })
        },

        formatSize(bytes) {
            if (bytes === 0) return '0 B'
            const k = 1024
            const sizes = ['B', 'KB', 'MB', 'GB']
            const i = Math.floor(Math.log(bytes) / Math.log(k))

            const size = parseFloat((bytes / k ** i).toFixed(2))
            return `${size}  ${sizes[i]}`
        },

        async upload(file) {
            file.newFileData.state = FILE_STATES.UPLOADING

            try {
                file.newFileData.uploadProgress = 0 // Reset progress

                const formData = new FormData()
                formData.append('image', file.newFileData.file)

                await this.$http.post(`/${this.organizationSlug}/relays/images?uuid=${file.id}`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                    onUploadProgress: (progressEvent) => {
                        file.newFileData.uploadProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
                    },
                    signal: file.newFileData.abortController.signal,
                })

                file.newFileData.state = FILE_STATES.COMPLETE
                this.$emit('file-uploaded', file.id)
                file.newFileData.uploadProgress = null
            } catch (error) {
                file.newFileData.state = FILE_STATES.ERROR
            } finally {
                file.newFileData.abortController = null
            }

            this.selectDefaultCoverImage()
        },

        async confirmRemoveImage(removeFileCallback, index) {
            const confirmed = await this.confirm({
                message: 'Are you sure you want to delete this image? This action cannot be undone.',
                acceptLabel: 'Delete',
                danger: true,
            })

            if (confirmed) {
                this.removeImage(removeFileCallback, index)
            }
        },

        removeImage(removeFileCallback, index) {
            const fileToRemove = this.files[index]

            let emitFileRemoved = true

            if (fileToRemove.isNew) {
                removeFileCallback(index - this.existingImages.length)
                this.files.splice(index, 1)

                fileToRemove.newFileData.abortController?.abort()
                URL.revokeObjectURL(fileToRemove.url)

                emitFileRemoved = fileToRemove.newFileData.state !== FILE_STATES.COMPLETE
            }

            if (emitFileRemoved) {
                this.$emit('file-removed', fileToRemove.id)
            }

            this.selectDefaultCoverImage()
        },
    },
}
</script>
<style scoped lang="scss">
.image-list {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    color: white;
    font-size: 14px;
}

.image-item {
    width: 100%;
    display: flex;
    flex-direction: column;
    border-bottom: 1px solid white;
    padding-bottom: 1em;

    .progress-container {
        margin-left: 36px;
    }

    .image-data {
        width: 100%;
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 1.25em;
        padding: 1rem 0;

        .star {
            width: 18px;
        }

        .image {
            height: 60px;
            width: 60px;
            display: flex;
            flex-direction: row;
            align-content: center;
            background-size: cover;
            background-position: center center;
            border-radius: 8px;
        }

        .info {
            display: flex;
            flex-direction: column;
            gap: 1em;
        }

        .status {
            font-size: 12px;
            border-radius: 8px;
            color: #141218;
            padding: 0.5rem 0.75rem;

            &.complete {
                background-color: #84db82;
            }

            &.uploading {
                background-color: #ffeaa1;
            }

            &.error {
                background-color: #f96a62;
            }
        }

        .delete-image-button {
            margin-left: auto;
            background-color: transparent;
            border-width: 0;
            cursor: pointer;
        }
    }
}

.file-uploader {
    border: 1px solid white;
    border-radius: 8px;
    padding: 0 1rem 1rem 1rem;
}

.choose-image-btn {
    margin-top: 1rem;
    display: flex;
    flex-direction: row;
    gap: 0.75em;
    justify-content: center;
}

.cover-image-button {
    background-color: transparent;
    border-width: 0px;
    cursor: pointer;
}

.drop-zone {
    height: 52px;
    border-radius: 8px;
    padding: 0.5rem 1rem;
    font-size: 15px;
    border: 2px dashed #979797;
    align-content: center;
}

/* Scoped styles to target the ProgressBar in this component */
:deep(.p-progressbar) {
    height: 4px;
}
</style>
